import React from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { COLUMN_MIN_WIDTH, ROW_NUMBER_COLUMN_WIDTH } from 'style/constants'
import Cell from 'components/spreadsheet/components/cell'
import {
  ITableRow,
  ITableViewColumn,
  ITableViewSort,
  ITableViewFilter,
  ICommentThreadStats,
  ICellValue,
  IContextMenuState,
  IUpdateTableViewCell
} from 'types'
import {
  SpreadsheetReducerActions,
  IViewPortColumn,
  ISelectedCell,
  ITableRowInGroup,
  ISelectedCellState,
  ISelectedCellRange,
  IFillDragState,
  ISelectedGroupCell
} from 'components/spreadsheet/types'
import {
  isRowInGroup,
  getRowValue,
  rowDataArrayToObject,
  getOriginalData,
  isColumnLockedByRules
} from 'components/spreadsheet/helpers/functions'
import { isInFillRange } from 'components/spreadsheet/helpers/fill'
import { getRowLevelColour, ROW_HOVER_FILL } from 'components/spreadsheet/helpers/colouring'
import { getRowLockState } from 'components/spreadsheet/helpers/locking'
import { getMatrixLockedCells } from 'components/spreadsheet/helpers/matrix'
import GroupCell from '../grouping/GroupCell'
import { useProject } from 'hooks/useProject'
import useHovered from 'hooks/useHovered'
import GroupLoadingCell from '../grouping/GroupLoading'
import { INDENT_PX, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { FIELD_COLUMN_ID, getTransposedDependencyRowData } from 'components/spreadsheet/helpers/transpose'

/** Resolve the effective column metadata for a cell.
 *  In transformed views, merges the original column's metadata (kind, locked,
 *  publicId, validation flags, etc.) with the synthetic column's presentational
 *  fields (name, width, sortOrder). The synthetic column ID is carried separately
 *  via Cell's `columnId` prop, so keeping the original publicId here preserves
 *  lookups keyed by the real column ID (duplicate validation, seenColumnValues).
 *  Formula/script/joined columns are treated as locked in transformed views since
 *  their values are computed and cannot be edited through the synthetic grid.
 *  In standard views, returns the viewport column unchanged. */
function getEffectiveCellColumn(
  viewportColumn: IViewPortColumn,
  originalColumn: ITableViewColumn | null
): ITableViewColumn {
  if (!originalColumn || viewportColumn.position === 0) return viewportColumn.column
  return {
    ...originalColumn,
    name: viewportColumn.column.name,
    width: viewportColumn.column.width,
    sortOrder: viewportColumn.column.sortOrder
  }
}

interface RowProps {
  rowNumber: number
  row: ITableRow | ITableRowInGroup
  prevRowId: string
  nextRowId: string
  rowHeight: number
  columns: IViewPortColumn[]
  allColumns: IViewPortColumn[] // All non-hidden columns (not virtualized) for selection range calculations
  filteredColumns: ITableViewFilter[]
  sortedColumns: ITableViewSort[]
  groupingEnabled: boolean
  uniqueNumber: number
  contextClick: (menuState: IContextMenuState) => void
  selectedCell: ISelectedCellState
  setSelectedCell: (selectedCell: ISelectedCellState) => void
  selectedCellRange: ISelectedCellRange
  setSelectedCellRange: (selectedCellRange: ISelectedCellRange) => void
  selectedColumnIds: Set<string>
  isSelectedRow: boolean
  onRowNumberSelect: (rowId: string, modifiers: { shiftKey: boolean; toggleKey: boolean }) => void
  openRowMenu: (rowId: string, rowNumber: number, event: React.MouseEvent) => void
  isContributor: boolean
  handleKeyDown: (event: React.KeyboardEvent, selectedCell: ISelectedCell) => void
  setSpreadsheetData?: React.Dispatch<SpreadsheetReducerActions>
  setDragOver: (dragOver: string) => void
  onRowDragStart?: (event: React.DragEvent<HTMLDivElement>, rowId: string, rowNumber: number) => void
  onRowDragEnd?: () => void
  dragStyle?: React.CSSProperties // Lift / slide overrides while a row is being dragged
  isAdmin?: boolean
  setCellValue?: (cells: IUpdateTableViewCell, onSuccess: () => void, onError: () => void) => void
  onGroupToggle: (groupKey: string) => void
  onGroupContext: (menuState: IContextMenuState) => void
  isFirstRow: boolean
  isLastRow: boolean
  groupedColumns: IViewPortColumn[]
  nonGroupedColumns: IViewPortColumn[]
  setExpandModal: (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => void
  setIsFocused?: (iFocused: boolean) => void
  setIsDragging?: (isDragging: boolean) => void
  groupRowSpans?: Map<string, number> // Maps "rowIndex-groupKeyIndex" to row span count
  rowIndex?: number // Index of this row in the rows array (for groupRowSpans lookup)
  commentsMap?: Map<string, ICommentThreadStats> // Pre-built map for O(1) comment lookups
  addRowOffset?: number // Cumulative pixel offset to subtract for shorter add-rows above this row
  fillDragState?: IFillDragState | null
  treeColumnWidth?: number // Width of the tree column in tree group view mode
  treeLeafColumn?: IViewPortColumn | null // First non-grouped column rendered as editable cell in tree column area
  selectedGroupCell: ISelectedGroupCell | null
  selectGroupCell: (groupCell: ISelectedGroupCell) => void
}

const Row: React.FC<RowProps> = ({
  rowNumber,
  row,
  prevRowId,
  nextRowId,
  rowHeight,
  groupingEnabled,
  contextClick,
  selectedCell,
  setSelectedCell,
  selectedCellRange,
  setSelectedCellRange,
  selectedColumnIds,
  isSelectedRow,
  onRowNumberSelect,
  openRowMenu,
  uniqueNumber,
  handleKeyDown,
  setSpreadsheetData,
  setDragOver,
  onRowDragStart,
  onRowDragEnd,
  dragStyle,
  isAdmin,
  isContributor,
  setCellValue,
  onGroupToggle,
  onGroupContext,
  isFirstRow,
  isLastRow,
  groupedColumns,
  nonGroupedColumns,
  allColumns,
  setExpandModal,
  setIsFocused,
  setIsDragging,
  groupRowSpans,
  rowIndex,
  commentsMap,
  addRowOffset = 0,
  fillDragState,
  treeColumnWidth = 0,
  treeLeafColumn,
  selectedGroupCell,
  selectGroupCell
}) => {
  // Read from context rather than props: React's development build diffs every changed prop on
  // each render, and a spreadsheetData prop would make it walk the whole rows array once per
  // visible row whenever rows change (seconds on a million rows). Context values are not diffed.
  const { spreadsheetData } = useDataContext()
  const { project } = useProject()

  // When bitmap filtering is active, startRowId/finalRowId/finalRowNumber must reference
  // visible rows, not the full unfiltered array, so keyboard navigation (Ctrl+Home/End) works.
  const bitmap = spreadsheetData.visibleBitmap
  let visibleStartRowId = spreadsheetData.rows[0]?.publicId
  let visibleFinalRowId = spreadsheetData.rows[spreadsheetData.rows.length - 1]?.publicId
  let visibleRowCount = spreadsheetData.rows.length
  if (bitmap) {
    visibleRowCount = spreadsheetData.visibleCount
    for (let i = 0; i < spreadsheetData.rows.length; i++) {
      if (bitmap[i]) {
        visibleStartRowId = spreadsheetData.rows[i].publicId
        break
      }
    }
    for (let i = spreadsheetData.rows.length - 1; i >= 0; i--) {
      if (bitmap[i]) {
        visibleFinalRowId = spreadsheetData.rows[i].publicId
        break
      }
    }
  }

  // Keeps the hover while the pointer is inside a dropdown one of this row's cells opened,
  // and lets go of it once that dropdown closes - see useHovered.
  const [hovered, claimHover] = useHovered()

  const canExpandRow = getViewCapabilities(spreadsheetData.viewDetails.type).rows.expandRow

  // Bubbles up from the row-number cell, which is the only part of the row that starts a row drag.
  // Anything else draggable inside a cell (a link, an image) doesn't set the row id, so is ignored.
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!onRowDragStart || !row || event.dataTransfer.getData('rowId') !== row.publicId) return
    onRowDragStart(event, row.publicId, rowNumber)
  }

  // Fires on the source row however the drag ends, including a cancel or a drop on nothing — the
  // cases where no drop runs and the drag preview would otherwise be left on screen.
  const handleDragEnd = () => {
    if (onRowDragEnd) onRowDragEnd()
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isAdmin) {
      event.preventDefault()
    }
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (isAdmin && row !== undefined && row !== null) {
      setDragOver(row.publicId)
      event.dataTransfer.dropEffect = 'move'
    }
  }

  const setCellValueCallback = (
    cells: IUpdateTableViewCell,
    onSuccess: () => void,
    onError: (message?: string) => void
  ) => {
    if (setCellValue && setSpreadsheetData) {
      cells.cells.forEach((cell) => {
        if (cell.oldValue !== undefined) {
          setSpreadsheetData({
            type: 'EDIT_CELL',
            columnId: cell.columnId,
            rowId: cell.rowId,
            value: { [cell.columnId]: cell.value as string },
            oldValue: cell.oldValue
          })
        }
      })

      setCellValue(cells, onSuccess, onError)
    }
  }

  const rowComments =
    spreadsheetData && row
      ? commentsMap
        ? commentsMap.get(row.publicId)
        : spreadsheetData.comments.find((comment: ICommentThreadStats) => comment.referenceId === row.publicId)
      : undefined

  const noRowComments = rowComments === undefined ? -1 : rowComments.openCommentThreads

  const noUnresolvedRowComments =
    rowComments === undefined ? -1 : rowComments.openCommentThreads - rowComments.resolvedCommentThreads

  // Pre-compute rowData as object once for all cells that need it (select/multiselect with dependencies)
  // This avoids calling rowDataArrayToObject multiple times per row
  const columnsNeedingRowData = new Set(
    nonGroupedColumns
      .filter(
        (vColumn) =>
          SELECT_COLUMN_TYPES.includes(vColumn.column.kind) &&
          vColumn.column.kindOptions?.tableOptions?.dependencies &&
          vColumn.column.kindOptions.tableOptions.dependencies.length > 0
      )
      .map((vColumn) => vColumn.column.publicId)
  )
  // In tree mode, the tree leaf column also needs rowData when it's a select with dependencies
  const treeLeafNeedsRowData =
    treeLeafColumn &&
    SELECT_COLUMN_TYPES.includes(treeLeafColumn.column.kind) &&
    !!treeLeafColumn.column.kindOptions?.tableOptions?.dependencies?.length
  // columnStore is mutated in place by editCell/bulkEditCells without ref change.
  // Including dataVersion in the expression makes React Compiler recompute after mutations.
  const rowDataAsObject =
    columnsNeedingRowData.size > 0 || treeLeafNeedsRowData
      ? rowDataArrayToObject(
          row.rowData,
          spreadsheetData.indexToColumnId,
          row._rowIndex,
          spreadsheetData.dataVersion >= 0 ? spreadsheetData.columnStore : null
        )
      : undefined

  // Pre-compute column position lookup for selection range calculations
  // This avoids O(n) filter/find/indexOf calls per cell inside the cell loop
  const allNonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
  // In tree mode, the tree leaf column is extracted from allColumns but still participates
  // in selection ranges — insert it at position 0 and offset the rest by 1.
  const columnPositionMap = new Map<string, number>()
  const treeLeafOffset = treeLeafColumn ? 1 : 0
  if (treeLeafColumn) {
    columnPositionMap.set(treeLeafColumn.column.publicId, 0)
  }
  allNonGroupedColumns.forEach((c, i) => columnPositionMap.set(c.column.publicId, i + treeLeafOffset))

  const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((col) => [col.publicId, col]))

  // In tree mode, exclude row-level colour rules that target grouped columns.
  // Those rules apply to tree group headers (CollapsedRow) instead of leaf data rows.
  const isTreeMode = treeColumnWidth > 0
  const effectiveColourSettings =
    isTreeMode && spreadsheetData.userConfiguration.groupSettings.length > 0
      ? spreadsheetData.userConfiguration.colourSettings.filter((setting) => {
          if (!setting.applyToEntireRow) return true
          return !spreadsheetData.userConfiguration.groupSettings.some((g) => g.columnId === setting.columnId)
        })
      : spreadsheetData.userConfiguration.colourSettings

  const hasRowLevelRules = effectiveColourSettings.some((setting) => setting.applyToEntireRow)
  const rowLockSettings = spreadsheetData.userConfiguration.rowLockSettings
  const isTransposeRow = !!row.originalColumnId
  const rowOriginalData = getOriginalData(spreadsheetData)
  const originalColumn = isTransposeRow
    ? (rowOriginalData.columns.find((c) => c.publicId === row.originalColumnId) ?? null)
    : null
  // Matrix rows are materialised synthetic rows: their rowData is indexed by
  // synthetic matrix column IDs, NOT the source-table column IDs that lock
  // rules reference. Detect them so we skip the per-row evaluation (which
  // would always miss) and instead delegate to ``getMatrixLockedCells``.
  const isMatrixRow =
    !isTransposeRow && !!spreadsheetData.viewContext && spreadsheetData.viewContext.coordinateMap.size > 0

  // For standard views: evaluate row-lock rules against this row's saved values.
  // For transpose views: each viewport column maps to a different original row.
  // For matrix views: each data cell maps to its own original row — handled
  // below by ``getMatrixLockedCells``.
  const rowLockState =
    rowLockSettings.length > 0 && row && !isTransposeRow && !isMatrixRow
      ? getRowLockState(
          rowLockSettings,
          row,
          columnLookup,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.columnValuesCount,
          spreadsheetData.columnStore
        )
      : null

  // For normal views: one row-level colour applies to the entire row
  // For transpose views: row-level rules are computed per-column using the original row data
  const rowLevelColour =
    hasRowLevelRules && row && !isTransposeRow
      ? getRowLevelColour(
          effectiveColourSettings,
          row,
          columnLookup,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.columnValuesCount,
          groupingEnabled,
          hovered ? ROW_HOVER_FILL : undefined,
          spreadsheetData.columnStore
        )
      : null

  // For transpose views, pre-compute row-level colours per pivot column.
  // Each transposed column maps to an original row — check row-level rules against that original row.
  let transposeColumnColours: Map<string, ReturnType<typeof getRowLevelColour>> | null = null
  if (hasRowLevelRules && isTransposeRow && rowOriginalData.coordinateMap) {
    const {
      rows: origRows,
      columnIdToIndex: origColIdToIndex,
      columns: origColumns,
      columnStore: origColumnStore,
      coordinateMap: coordMap
    } = rowOriginalData

    const origColumnLookup = new Map(origColumns.map((col) => [col.publicId, col]))
    const origRowMap = new Map(origRows.map((r) => [r.publicId, r]))
    const result = new Map<string, ReturnType<typeof getRowLevelColour>>()

    for (const viewportCol of nonGroupedColumns) {
      const key = `${row.publicId}:${viewportCol.column.publicId}`
      const coord = coordMap.get(key)
      if (!coord?.originalRowId) continue

      const origRow = origRowMap.get(coord.originalRowId)
      if (!origRow) continue

      const colour = getRowLevelColour(
        effectiveColourSettings,
        origRow,
        origColumnLookup,
        origColIdToIndex,
        spreadsheetData.columnValuesCount,
        groupingEnabled,
        hovered ? ROW_HOVER_FILL : undefined,
        origColumnStore
      )
      if (colour.matchedRuleIndex >= 0) {
        result.set(viewportCol.column.publicId, colour)
      }
    }

    transposeColumnColours = result.size > 0 ? result : null
  }

  // For transpose views, pre-compute lock state per pivot column (= per original row).
  // The transposed row maps to a single original column — that's `originalColumn`.
  // A viewport column's cell here is locked when:
  //   - the original row matches a rule with applyToEntireRow=true (whole vertical strip locked), or
  //   - the original row matches a rule with applyToEntireRow=false AND the rule's criteria
  //     column is `originalColumn` (i.e. the cell at this transposed-row × viewport-column).
  let transposeColumnLockStates: Map<string, ReturnType<typeof getRowLockState>> | null = null
  if (rowLockSettings.length > 0 && isTransposeRow && rowOriginalData.coordinateMap) {
    const {
      rows: origRows,
      columnIdToIndex: origColIdToIndex,
      columns: origColumns,
      columnStore: origColumnStore,
      coordinateMap: coordMap
    } = rowOriginalData

    const origColumnLookup = new Map(origColumns.map((col) => [col.publicId, col]))
    const origRowMap = new Map(origRows.map((r) => [r.publicId, r]))
    const result = new Map<string, ReturnType<typeof getRowLockState>>()

    for (const viewportCol of nonGroupedColumns) {
      const key = `${row.publicId}:${viewportCol.column.publicId}`
      const coord = coordMap.get(key)
      if (!coord?.originalRowId) continue

      const origRow = origRowMap.get(coord.originalRowId)
      if (!origRow) continue

      const lockState = getRowLockState(
        rowLockSettings,
        origRow,
        origColumnLookup,
        origColIdToIndex,
        spreadsheetData.columnValuesCount,
        origColumnStore
      )
      if (lockState.rowLocked || lockState.lockedColumnIds.size > 0) {
        result.set(viewportCol.column.publicId, lockState)
      }
    }

    transposeColumnLockStates = result.size > 0 ? result : null
  }

  // For matrix views, delegate row-lock evaluation to the matrix helper —
  // see ``getMatrixLockedCells`` for the per-source-row resolution logic.
  const matrixLockedCells =
    isMatrixRow && rowLockSettings.length > 0 && rowOriginalData.coordinateMap
      ? getMatrixLockedCells({
          matrixRow: row,
          viewportColumnIds: nonGroupedColumns.map((vc) => vc.column.publicId),
          rowLockSettings,
          coordinateMap: rowOriginalData.coordinateMap,
          origColumns: rowOriginalData.columns,
          origRows: rowOriginalData.rows,
          origColumnIdToIndex: rowOriginalData.columnIdToIndex,
          origColumnStore: rowOriginalData.columnStore,
          columnValuesCount: spreadsheetData.columnValuesCount
        })
      : null

  // In transposed view, each transposed column represents a different original row.
  // For select dependencies we need rowData keyed by *original* column IDs with values
  // from the *same original row*. Pre-compute a map: transposedColumnId → original rowData object.
  const transposeRowHasSelectDeps =
    isTransposeRow &&
    originalColumn &&
    SELECT_COLUMN_TYPES.includes(originalColumn.kind) &&
    !!originalColumn.kindOptions?.tableOptions?.dependencies?.length

  const transposeRowDataByColumn =
    transposeRowHasSelectDeps && rowOriginalData.coordinateMap
      ? getTransposedDependencyRowData(row, nonGroupedColumns, rowOriginalData)
      : null

  // In transposed views the Field cell (left-most cell of each row) is the row label —
  // it represents the original column. Style it with that column's header colours so
  // it reads as a header, mirroring how column headers are styled in the normal view.
  const transposeFieldHeaderColour =
    isTransposeRow && originalColumn
      ? {
          backgroundColour: originalColumn.headerBackgroundColor || project.defaultHeaderBackgroundColor,
          fontColour: originalColumn.headerTextColor || project.defaultHeaderTextColor
        }
      : null

  // The `isRowInGroup` function is required because although column may be marked as group, the useEffect
  // which actually puts the rows into the group may not have run yet, this normally happens when we're
  // still loading data into the frontend
  const groupedColumnsLoaded = isRowInGroup(row) ? groupedColumns : []
  const groupedColumnsYetToLoad = isRowInGroup(row) ? [] : groupedColumns

  // Map column publicId → groupSettings index (which matches groupKeys ordering).
  // Viewport columns may be a subset when group columns scroll out of view,
  // so we can't use the viewport array index to look up groupKeys.
  const groupSettingsIndexMap = new Map(
    spreadsheetData.userConfiguration.groupSettings.map((g, idx) => [g.columnId, idx])
  )

  // This will be empty if there is no grouping applied
  const groupedColumnElements = groupedColumnsLoaded.map((vColumn, viewportIndex) => {
    const rowInGroup = row as ITableRowInGroup
    const groupKeyIndex = groupSettingsIndexMap.get(vColumn.column.publicId) ?? viewportIndex
    const groupKey = rowInGroup.groupKeys[groupKeyIndex]
    const value = getRowValue(
      row,
      vColumn.column.publicId,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnStore
    )
    const shouldShowValue = rowInGroup.groupsToRender.includes(groupKey) && rowInGroup.start

    // Calculate span width: sum widths of this column plus subsequent group columns
    // until we hit another column that will show its own value
    let spanWidth = vColumn.column.width
    if (shouldShowValue) {
      for (let i = viewportIndex + 1; i < groupedColumnsLoaded.length; i++) {
        const nextColumn = groupedColumnsLoaded[i]
        const nextGroupKeyIndex = groupSettingsIndexMap.get(nextColumn.column.publicId) ?? i
        const nextGroupKey = rowInGroup.groupKeys[nextGroupKeyIndex]
        // Stop if the next column will show its own value
        if (rowInGroup.groupsToRender.includes(nextGroupKey) && rowInGroup.start) {
          break
        }
        spanWidth += nextColumn.column.width
      }
    }

    // Calculate span height (number of rows this group label can span)
    const rowSpanCount =
      shouldShowValue && groupRowSpans && rowIndex !== undefined
        ? (groupRowSpans.get(`${rowIndex}-${groupKeyIndex}`) ?? 1)
        : 1

    return (
      <GroupCell
        key={vColumn.column.publicId}
        column={vColumn}
        rowHeight={rowHeight}
        colourSettings={spreadsheetData.userConfiguration.colourSettings}
        onGroupToggle={onGroupToggle}
        groupKey={groupKey}
        value={value}
        canCollapse={true}
        onContext={onGroupContext}
        shouldShowValue={shouldShowValue}
        columnValuesCount={spreadsheetData.columnValuesCount}
        spanWidth={spanWidth}
        rowSpanCount={rowSpanCount}
        row={row}
        rowLevelColour={rowLevelColour}
        groupItemKey={groupKey}
        inSelectedColumn={selectedColumnIds.has(vColumn.column.publicId)}
        selected={selectedGroupCell?.columnId === vColumn.column.publicId && selectedGroupCell?.groupKey === groupKey}
        onSelect={selectGroupCell}
        isFirstRow={isFirstRow}
      />
    )
  })

  // Renders placeholder group cells when we're still loading data into the frontend
  const groupLoadingCells = groupedColumnsYetToLoad.map((vColumn) => (
    <GroupLoadingCell
      key={vColumn.column.publicId}
      column={vColumn}
      rowHeight={rowHeight}
      value={isFirstRow ? 'Loading group...' : ''}
    />
  ))

  return (
    <div
      key={`row/${row.publicId}/${uniqueNumber}`}
      id={`row/${row.publicId}/${uniqueNumber}`}
      className={`absolute top-0 inline-flex m-0 p-0 h-auto ${hovered ? 'bg-surface-muted' : 'bg-surface'}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      // No onFocus counterpart: the hover only paints the background and reveals the expand
      // icon, which the keyboard reaches through the row menu.
      //eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={claimHover}
      onMouseMove={claimHover}
      style={{
        contain: 'style',
        width: 'var(--grid-width)',
        top: `calc(var(--header-height) + ${rowNumber} * var(--row-height) + var(--row-top-offset) - ${addRowOffset}px)`,
        zIndex: undefined,
        ...dragStyle
      }}
    >
      {/* cell that displays the current row number */}
      <Cell
        width={ROW_NUMBER_COLUMN_WIDTH}
        cumulativeWidth={0}
        value={(rowNumber + 1).toString()}
        color={'var(--color-muted)'}
        align={'center'}
        rowHeight={rowHeight}
        loading={!row}
        rowNumber={rowNumber}
        rowId={row ? row.publicId : undefined}
        kind={'text'}
        scriptEnabled={false}
        formulaEnabled={false}
        isJoined={false}
        contextClick={contextClick}
        frozen={true}
        canDrag={isAdmin && !isMatrixRow}
        isContributor={false}
        isAdmin={false}
        uniqueNumber={uniqueNumber}
        noComments={noRowComments}
        unresolvedComments={noUnresolvedRowComments}
        isFirstRow={isFirstRow}
        isLastRow={isLastRow}
        useGreyBackground={groupingEnabled}
        setSelectedCell={setSelectedCell}
        setCellValue={setCellValue}
        setSelectedCellRange={setSelectedCellRange}
        editing={false}
        setExpandModal={setExpandModal}
        showExpandIcon={hovered && canExpandRow}
        type={spreadsheetData.viewDetails.type}
        colourSettings={spreadsheetData.userConfiguration.colourSettings}
        columnValuesCount={spreadsheetData.columnValuesCount}
        allowComments={spreadsheetData.tableDetails.allowComments}
        row={row}
        setIsFocused={setIsFocused}
        setIsDragging={setIsDragging}
        startRowId={visibleStartRowId}
        finalRowId={visibleFinalRowId}
        finalRowNumber={visibleRowCount}
        startColumnId={
          spreadsheetData.viewDetails.columns.length > 0 ? spreadsheetData.viewDetails.columns[0].publicId : undefined
        }
        finalColumnId={
          spreadsheetData.viewDetails.columns.length > 0
            ? spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
            : undefined
        }
        finalColumnNumber={spreadsheetData.viewDetails.columns.length}
        rowLevelColour={rowLevelColour}
        inSelectedRow={isSelectedRow}
        onRowNumberSelect={(modifiers) => onRowNumberSelect(row.publicId, modifiers)}
        openRowMenu={(event) => openRowMenu(row.publicId, rowNumber, event)}
      />

      {treeColumnWidth > 0 && treeLeafColumn ? (
        <Cell
          key={`tree-leaf-${row.publicId}-${treeLeafColumn.column.publicId}`}
          width={treeColumnWidth}
          cumulativeWidth={ROW_NUMBER_COLUMN_WIDTH}
          // Indent the text inside the cell via the --cell-pl CSS var (read by the
          // value slot in cell/styles.ts). The cell itself keeps its full footprint,
          // so the focus outline and selection border still hug a normal-shaped cell.
          // `treeDepth` is set by the tree flattening hook for tree group view rows.
          treeIndentPx={(row.treeDepth ?? 0) * INDENT_PX + 8}
          value={getRowValue(
            row,
            treeLeafColumn.column.publicId,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.columnStore
          )}
          rowNumber={rowNumber}
          rowHeight={rowHeight}
          rowId={row.publicId}
          prevRowId={prevRowId}
          nextRowId={nextRowId}
          scriptEnabled={treeLeafColumn.column.scriptEnabled}
          formulaEnabled={treeLeafColumn.column.formulaEnabled}
          columnNumber={0}
          columnId={treeLeafColumn.column.publicId}
          prevColumnId=""
          nextColumnId={nonGroupedColumns.length > 0 ? nonGroupedColumns[0].column.publicId : ''}
          setSelectedCell={setSelectedCell}
          kind={treeLeafColumn.column.kind}
          isJoined={treeLeafColumn.column.isJoined}
          contextClick={contextClick}
          frozen={true}
          isLastFrozenCell={false}
          isContributor={isContributor}
          isAdmin={!!isAdmin}
          handleKeyDown={handleKeyDown}
          uniqueNumber={uniqueNumber}
          locked={
            isColumnLockedByRules(rowLockSettings, treeLeafColumn.column.publicId) ||
            treeLeafColumn.column.isJoined ||
            treeLeafColumn.column.isSynced ||
            !!rowLockState?.rowLocked ||
            !!rowLockState?.lockedColumnIds.has(treeLeafColumn.column.publicId)
          }
          setCellValue={setCellValueCallback}
          columnName={treeLeafColumn.column.name}
          isFirstRow={isFirstRow}
          isLastRow={isLastRow}
          selected={selectedCell.columnId === treeLeafColumn.column.publicId && selectedCell.rowId === row.publicId}
          inSelectedColumn={selectedColumnIds.has(treeLeafColumn.column.publicId)}
          inSelectedRow={isSelectedRow}
          searched={
            row &&
            spreadsheetData.searchedRows.has(row.publicId) &&
            spreadsheetData.searchedRows.get(row.publicId)!.includes(treeLeafColumn.column.publicId)
          }
          inSelectedCellRange={(() => {
            const inRowRange =
              (selectedCellRange.endRowIndex !== -1 &&
                rowNumber >= selectedCell.rowNumber &&
                rowNumber <= selectedCellRange.endRowIndex) ||
              (rowNumber <= selectedCell.rowNumber && rowNumber >= selectedCellRange.endRowIndex)
            if (!inRowRange) return false
            if (selectedCellRange.endColumnId) {
              const anchorPos = columnPositionMap.get(selectedCell.columnId)
              const endPos = columnPositionMap.get(selectedCellRange.endColumnId)
              const currentPos = columnPositionMap.get(treeLeafColumn.column.publicId)
              if (anchorPos !== undefined && endPos !== undefined && currentPos !== undefined) {
                const minPos = Math.min(anchorPos, endPos)
                const maxPos = Math.max(anchorPos, endPos)
                return currentPos >= minPos && currentPos <= maxPos
              }
            } else if (selectedCellRange.endColumnIndex !== -1) {
              return (
                (0 <= selectedCell.columnNumber && 0 >= selectedCellRange.endColumnIndex) ||
                (0 >= selectedCell.columnNumber && 0 <= selectedCellRange.endColumnIndex)
              )
            }
            return false
          })()}
          setSelectedCellRange={setSelectedCellRange}
          editing={
            selectedCell.columnId === treeLeafColumn.column.publicId &&
            selectedCell.rowId === row.publicId &&
            selectedCell.editing
          }
          tempValue={
            selectedCell.columnId === treeLeafColumn.column.publicId && selectedCell.rowId === row.publicId
              ? selectedCell.value
              : undefined
          }
          setExpandModal={setExpandModal}
          setIsFocused={setIsFocused}
          setIsDragging={setIsDragging}
          rowData={treeLeafNeedsRowData ? rowDataAsObject : undefined}
          columnValuesCount={spreadsheetData.columnValuesCount}
          column={treeLeafColumn.column}
          type={spreadsheetData.viewDetails.type}
          colourSettings={spreadsheetData.userConfiguration.colourSettings}
          startRowId={visibleStartRowId}
          finalRowId={visibleFinalRowId}
          finalRowNumber={visibleRowCount}
          startColumnId={
            spreadsheetData.viewDetails.columns.length > 0 ? spreadsheetData.viewDetails.columns[0].publicId : undefined
          }
          finalColumnId={
            spreadsheetData.viewDetails.columns.length > 0
              ? spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
              : undefined
          }
          finalColumnNumber={spreadsheetData.viewDetails.columns.length}
          allowComments={spreadsheetData.tableDetails.allowComments}
          row={row}
          hovered={hovered}
          rowLevelColour={rowLevelColour}
        />
      ) : (
        <>
          {groupLoadingCells}
          {groupedColumnElements}
        </>
      )}

      {/* all other cells */}
      {nonGroupedColumns.map((viewportColumn: IViewPortColumn, columnNumber: number) => {
        const prevColumn = nonGroupedColumns[columnNumber - 1]
        const nextColumn = nonGroupedColumns[columnNumber + 1]

        const value: ICellValue = getRowValue(
          row,
          viewportColumn.column.publicId,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.columnStore
        )

        const selected = selectedCell.columnId === viewportColumn.column.publicId && selectedCell.rowId === row.publicId

        const inSelectedRowRange =
          (selectedCellRange.endRowIndex !== -1 &&
            rowNumber >= selectedCell.rowNumber &&
            rowNumber <= selectedCellRange.endRowIndex) ||
          (rowNumber <= selectedCell.rowNumber && rowNumber >= selectedCellRange.endRowIndex)

        // Use column IDs for range checking when endColumnId is available to avoid virtualization index mismatch
        // We use columnPositionMap (pre-computed above) for O(1) lookups instead of O(n) find/indexOf per cell
        let inSelectedColumnRange = false
        if (selectedCellRange.endColumnId) {
          const anchorPos = columnPositionMap.get(selectedCell.columnId)
          const endPos = columnPositionMap.get(selectedCellRange.endColumnId)
          const currentPos = columnPositionMap.get(viewportColumn.column.publicId)

          if (anchorPos !== undefined && endPos !== undefined && currentPos !== undefined) {
            const minPos = Math.min(anchorPos, endPos)
            const maxPos = Math.max(anchorPos, endPos)
            inSelectedColumnRange = currentPos >= minPos && currentPos <= maxPos
          }
        } else if (selectedCellRange.endColumnIndex !== -1) {
          // Fall back to index-based comparison when no endColumnId
          inSelectedColumnRange =
            (columnNumber <= selectedCell.columnNumber && columnNumber >= selectedCellRange.endColumnIndex) ||
            (columnNumber >= selectedCell.columnNumber && columnNumber <= selectedCellRange.endColumnIndex)
        }

        const inSelectedCellRange = inSelectedRowRange && inSelectedColumnRange

        const inFillRange = fillDragState
          ? isInFillRange(rowNumber, viewportColumn.column.publicId, fillDragState, columnPositionMap)
          : false

        const effectiveCol = getEffectiveCellColumn(viewportColumn, originalColumn)

        return (
          <Cell
            key={`$row-${row.publicId}-column-${viewportColumn.column.publicId}`}
            width={viewportColumn.column.width}
            cumulativeWidth={viewportColumn.left}
            value={value}
            rowNumber={rowNumber}
            rowHeight={rowHeight}
            rowId={row.publicId}
            prevRowId={prevRowId}
            nextRowId={nextRowId}
            scriptEnabled={effectiveCol.scriptEnabled}
            formulaEnabled={effectiveCol.formulaEnabled}
            columnNumber={columnNumber}
            columnId={viewportColumn.column.publicId}
            prevColumnId={
              prevColumn && prevColumn.column
                ? prevColumn.column.publicId
                : treeLeafColumn
                  ? treeLeafColumn.column.publicId
                  : ''
            }
            nextColumnId={nextColumn && nextColumn.column ? nextColumn.column.publicId : ''}
            setSelectedCell={setSelectedCell}
            kind={effectiveCol.kind}
            isJoined={effectiveCol.isJoined}
            contextClick={contextClick}
            frozen={viewportColumn.isFrozen}
            isLastFrozenCell={viewportColumn.isLastFrozenColumn}
            isContributor={isContributor}
            isAdmin={!!isAdmin}
            handleKeyDown={handleKeyDown}
            uniqueNumber={uniqueNumber}
            locked={(() => {
              // Sparse matrix data cells have no source row to write to — typed edits would
              // be silently dropped by mapMatrixCells.
              const isSparseMatrixCell =
                spreadsheetData.viewContext?.matrixSparseCells?.has(
                  `${row.publicId}:${viewportColumn.column.publicId}`
                ) ?? false
              // Structurally read-only synthetic columns in transformed views (matrix
              // row-labels / read-only-source data columns, transpose field column) —
              // synthetic columns drop the intrinsic flags, so the adapter flags them.
              const isReadOnlyColumn =
                spreadsheetData.viewContext?.readOnlyColumns?.has(viewportColumn.column.publicId) ?? false
              if (
                isColumnLockedByRules(rowLockSettings, effectiveCol.publicId) ||
                viewportColumn.isGrouped ||
                effectiveCol.isSynced ||
                isSparseMatrixCell ||
                isReadOnlyColumn
              ) {
                return true
              }
              if (isTransposeRow) {
                const transposeLockState = transposeColumnLockStates?.get(viewportColumn.column.publicId)
                if (transposeLockState?.rowLocked) return true
                if (originalColumn && transposeLockState?.lockedColumnIds.has(originalColumn.publicId)) return true
                return false
              }
              if (isMatrixRow) {
                return matrixLockedCells?.has(`${row.publicId}:${viewportColumn.column.publicId}`) ?? false
              }
              if (rowLockState?.rowLocked) return true
              if (rowLockState?.lockedColumnIds.has(effectiveCol.publicId)) return true
              return false
            })()}
            setCellValue={setCellValueCallback}
            columnName={viewportColumn.column.name}
            isFirstRow={isFirstRow}
            isLastRow={isLastRow}
            selected={selected}
            inSelectedColumn={selectedColumnIds.has(viewportColumn.column.publicId)}
            inSelectedRow={isSelectedRow}
            searched={
              row &&
              spreadsheetData.searchedRows.has(row.publicId) &&
              spreadsheetData.searchedRows.get(row.publicId)!.includes(viewportColumn.column.publicId)
            }
            inSelectedCellRange={inSelectedCellRange}
            inFillRange={inFillRange}
            isFillDragging={!!fillDragState}
            setSelectedCellRange={setSelectedCellRange}
            editing={selected && selectedCell.editing}
            tempValue={selected ? selectedCell.value : undefined}
            setExpandModal={setExpandModal}
            setIsFocused={setIsFocused}
            setIsDragging={setIsDragging}
            rowData={
              transposeRowDataByColumn
                ? transposeRowDataByColumn.get(viewportColumn.column.publicId)
                : columnsNeedingRowData.has(viewportColumn.column.publicId)
                  ? rowDataAsObject
                  : undefined
            }
            columnValuesCount={spreadsheetData.columnValuesCount}
            column={effectiveCol}
            type={spreadsheetData.viewDetails.type}
            colourSettings={spreadsheetData.userConfiguration.colourSettings}
            startRowId={visibleStartRowId}
            finalRowId={visibleFinalRowId}
            finalRowNumber={visibleRowCount}
            startColumnId={
              spreadsheetData.viewDetails.columns.length > 0
                ? spreadsheetData.viewDetails.columns[0].publicId
                : undefined
            }
            finalColumnId={
              spreadsheetData.viewDetails.columns.length > 0
                ? spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
                : undefined
            }
            finalColumnNumber={spreadsheetData.viewDetails.columns.length}
            allowComments={spreadsheetData.tableDetails.allowComments}
            row={row}
            hovered={hovered}
            rowLevelColour={transposeColumnColours?.get(viewportColumn.column.publicId) ?? rowLevelColour}
            headerColourOverride={
              viewportColumn.column.publicId === FIELD_COLUMN_ID ? transposeFieldHeaderColour : null
            }
            cellIssue={
              spreadsheetData.viewContext?.matrixCollisionCells?.has(
                `${row.publicId}:${viewportColumn.column.publicId}`
              )
                ? viewportColumn.column.publicId.startsWith('matrix-row-label::')
                  ? '' // For now remove the error, maybe add back later
                  : 'Multiple source rows match this cell. Resolve the duplicate column-label values in the source to display a value.'
                : null
            }
          />
        )
      })}
      {/* Under the header's +: a quiet grey lane rather than an empty white
          column, and without row lines, so it reads as somewhere to add a
          column, not as data. */}
      {spreadsheetData.isAdmin && (
        <div
          aria-hidden
          data-testid="new-column-lane"
          className="absolute top-0 right-0 bg-surface-sunken pointer-events-none"
          style={{ width: COLUMN_MIN_WIDTH, height: 'var(--row-height)' }}
        />
      )}
    </div>
  )
}

// Memoised on props: the parent re-renders on every scroll frame (the viewport window moves), but
// a row whose props have not changed must not re-render with it. Context changes still
// reach it (memo only compares props).
export default React.memo(Row)
